import cloneDeep from 'lodash/cloneDeep';
import type { FieldValue, ItemMetaData } from '../dto/fieldValue';
import type { AlternativeValue, FieldDefinition, MdfFieldEntity } from './fieldDefinition';
import type { MetadataDeltaPayload } from '../dto/mdfModels';
import { FieldType } from './fieldDefinition';
import type { MetaDataSchema } from './metadataSchema';
import { DEFAULT_MDF_ID, FOLDER_DEFAULT_MDF_ID } from './mdfDefaults';
import { invariant } from '../util/invariant';

/**
 * Resolves circular dependencies and returns selectable subtype schemas.
 *
 * If we have four subtypes A, B, C, D. Now if we use B as a subtype of A,
 * then B will not be selectable as subtype in C or D.
 *
 * Subtype schemas that are referenced in main and common schemas will also
 * be removed when the selected mdf schema is of subtype.
 *
 * @param subtypeSchemas Subtype schemas.
 * @param mainSchemas Schemas that do not have isSubtype property or isSubtype is false.
 * @param selectedMdf Schema that is selected from the Mdf dropdown.
 * @returns Selctable subtype schemas.
 */
export const extractSelectableSubtypes = (subtypeSchemas: MetaDataSchema[], mainSchemas: MetaDataSchema[], selectedMdf: MetaDataSchema): MetaDataSchema[] => {
  const clonedSubtypeSchemaList: MetaDataSchema[] = [];

  clonedSubtypeSchemaList.push(...cloneDeep(subtypeSchemas).filter(schema => schema.label !== selectedMdf.label));
  const dependentSchemas = [...extractDependentSubtypeLabels(clonedSubtypeSchemaList, selectedMdf)];

  if (selectedMdf.isSubtype) {
    /*
      Special case: To filter out the subtypes that are referenced in the Main and Common type,
      when the selectedMdf is of subtype.
    */
    dependentSchemas.push(...extractDependentSubtypeLabels(mainSchemas, selectedMdf));
  }

  return clonedSubtypeSchemaList.filter(sMdf => !dependentSchemas.includes(sMdf.label));
};

const extractDependentSubtypeLabels = (schemas: MetaDataSchema[], selectedMdf: MetaDataSchema): AlternativeValue[] => {
  const subtypeSchemaLabels: AlternativeValue[] = [];

  const dependencyDict: { [id: string]: AlternativeValue[] } = {};
  for (const schema of schemas) {
    if (schema.label === selectedMdf.label) {
      continue;
    }
    schema.fields.forEach(field => {
      if (field.type === FieldType.SubType) {
        dependencyDict[schema.label] = field.alternatives?.map(alt => alt.value) ?? [];
      }
    });
  }

  for (const key in dependencyDict) {
    subtypeSchemaLabels.push(...dependencyDict[key]!);
  }

  let currentLabel = selectedMdf.label;
  let headFound = false;
  while (!headFound) {
    let newCurrnetLabelFound = false;
    for (const key in dependencyDict) {
      if (dependencyDict[key]!.findIndex(dep => currentLabel === dep) >= 0) {
        currentLabel = key;
        subtypeSchemaLabels.push(currentLabel);
        newCurrnetLabelFound = true;
        break;
      }
    }
    if (!newCurrnetLabelFound) {
      headFound = true;
    }
  }
  subtypeSchemaLabels.push(currentLabel);
  return [...new Set(subtypeSchemaLabels)];
};


type MinimalSchemaType = { id: string, label: string, fields?: MdfFieldEntity[], flavor?: string };
/**
 * Returns the mdf schemas that are used by the given
 * formData, by starting out by considering the formData under
 * the default schema and (if provided) the given startSchema.
 */
export function getUsedSchemasForFormData<SchemaType extends MinimalSchemaType>(
  formData: Record<string, FieldValue | null>,
  startSchema: SchemaType | null,
  allMdfs: SchemaType[],
): SchemaType[] {
  const schemaQueue: SchemaType[] = [];
  // Folder metadata does not have common schema concept and always has a hardcoded default schema
  if (startSchema?.flavor === undefined && startSchema?.id !== FOLDER_DEFAULT_MDF_ID) {
    const defaultSchema = allMdfs.find(s => s.id === DEFAULT_MDF_ID);
    if (defaultSchema) {
      // Include the default schema if there is one. But for
      // non-item metadata, such as for custom actions or analytics,
      // there is no concept of a default schema.
      schemaQueue.push(defaultSchema);
    }
  }
  if (startSchema && startSchema.id !== DEFAULT_MDF_ID) {
    schemaQueue.push(startSchema);
  }
  const schemasByLabel: Record<string, SchemaType> = {};
  for (const schema of allMdfs) {
    schemasByLabel[schema.label] = schema;
  }

  // To make sure we don't go into an infinite loop if there are loops
  // in the formData we track which schemas we've already seen.
  const seenByLabel: Record<string, SchemaType> = {};

  while (schemaQueue.length > 0) {
    const currentSchema = schemaQueue.pop();
    if (!currentSchema || seenByLabel[currentSchema.label]) {
      continue;
    }
    seenByLabel[currentSchema.label] = currentSchema;

    for (const field of currentSchema.fields ?? []) {
      if (field.type === FieldType.SubType) {
        const fieldVal = formData[field.id] ?? formData[field.fieldId];
        const subSchema = typeof fieldVal === 'string' && schemasByLabel[fieldVal];
        if (subSchema) {
          schemaQueue.push(subSchema);
        }
      }
    }
  }

  return Object.values(seenByLabel);
}

/**
 * Returns field definitions, used schemas, and the main schema ID for given metadata object.
 * Requires all metadata schemas for the whole tenant because the metadata can contain subtype
 * fields that reference other schemas.
 *
 * @param metadata - The metadata or metadata delta payload.
 * @param allMdfs - An array of all schema types for a tenant.
 * @returns An object containing:
 *    - usedSchemas - The schemas (main and sub types) that are used.
 *    - mainSchemaId - The ID of the main schema, or null if not found.
 *    - fieldDefinitions - A flat list of field definitions for each field in the metadata.formData object.
 *    - fieldMap - A key value map of field definitions where the keys are both the 'fieldId's and the GUIDs of the fields.
 */
export function getFieldDefinitionsForMetadata<SchemaType extends MinimalSchemaType>(
  metadata: ItemMetaData | MetadataDeltaPayload,
  allMdfs: SchemaType[],
): {
  fieldDefinitions: FieldDefinition[],
  usedSchemas: SchemaType[],
  mainSchemaId: string | null,
  fieldMap: Record<string, FieldDefinition>,
} {
  const mainSchema = allMdfs.find(s => s.id === metadata.formId || s.label === metadata.formId);
  const usedSchemas = getUsedSchemasForFormData(metadata.formData, mainSchema ?? null, allMdfs);

  const fieldMap: Record<string, FieldDefinition> = {};
  for (const schema of usedSchemas) {
    for (const field of schema.fields ?? []) {
      // If there are multiple fields across schemas for the same fieldId then avoid overriding field definitions
      // except for the main schema, which should take precedence.
      const isMainSchema = mainSchema?.id === schema.id || mainSchema?.label === schema.label;
      if (!isMainSchema && fieldMap[field.fieldId]) {
        continue;
      }

      fieldMap[field.id] = { ...field, schemaId: schema.id };
      fieldMap[field.fieldId] = { ...field, schemaId: schema.id };
    }
  }

  const fieldDefinitions: FieldDefinition[] = [];
  for (const key of Object.keys(metadata.formData)) {
    const field = fieldMap[key];
    if (!field) {
      continue;
    }
    fieldDefinitions.push(field);
  }

  return { fieldDefinitions, usedSchemas, mainSchemaId: mainSchema?.id ?? null, fieldMap };
}


export function getSchemaIdsToResolveFromFields(fields: MdfFieldEntity[]) {
  return fields.filter(f => f.type === FieldType.SubType)
    .flatMap(f => f.alternatives?.map(a => a.value))
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
}

/**
 * For given 'mainSchemaIdOrLabel' returns the main schema and all the nested subtype schemas that are referenced
 */
export function getMainAndSubtypeSchemas<SchemaType extends MinimalSchemaType>(
  allSchemas: SchemaType[],
  mainSchemaIdOrLabel: string = DEFAULT_MDF_ID,
) {
  const schemasByLabelAndId: Record<string, SchemaType> = {};
  for (const schema of allSchemas) {
    schemasByLabelAndId[schema.label] = schema;
    schemasByLabelAndId[schema.id] = schema;
  }

  const defaultSchema = schemasByLabelAndId[DEFAULT_MDF_ID];
  invariant(defaultSchema, 'Expected default schema');
  const customMainSchema = defaultSchema.id === mainSchemaIdOrLabel || defaultSchema.label === mainSchemaIdOrLabel
    ? null
    : schemasByLabelAndId[mainSchemaIdOrLabel];

  const resolvedSchemas = [defaultSchema];
  if (customMainSchema) {
    resolvedSchemas.push(customMainSchema);
  }

  const seenByLabelOrId: Record<string, boolean> = {};
  for (const schema of resolvedSchemas) {
    seenByLabelOrId[schema.label] = true;
    seenByLabelOrId[schema.id] = true;
  }

  const schemaIdsToResolve = getSchemaIdsToResolveFromFields([...(defaultSchema.fields ?? []), ...(customMainSchema?.fields ?? [])]);

  while (schemaIdsToResolve.length > 0) {
    const schemaId = schemaIdsToResolve.pop();
    if (!schemaId || seenByLabelOrId[schemaId]) {
      continue;
    }
    seenByLabelOrId[schemaId] = true;

    const schema = schemasByLabelAndId[schemaId];
    if (!schema) {
      continue;
    }
    seenByLabelOrId[schema.label] = true;
    seenByLabelOrId[schema.id] = true;

    resolvedSchemas.push(schema);
    schemaIdsToResolve.push(...getSchemaIdsToResolveFromFields(schema.fields ?? []));
  }

  return resolvedSchemas;
}

/**
 * Returns field map for the given main schema and all its subtype schemas.
 * This includes fields from the main schema, default schema, and all referenced subtype schemas.
 *
 * @param mainSchemaIdOrLabel - The ID or label of the main schema.
 * @param allMdfs - An array of all schema types for a tenant.
 * @returns A record mapping field IDs and fieldIds to their definitions.
 */
export function getFieldMapForMainAndSubtypes<SchemaType extends MinimalSchemaType>(
  mainSchemaIdOrLabel: string,
  allMdfs: SchemaType[],
): Record<string, FieldDefinition> {
  const usedSchemas = getMainAndSubtypeSchemas(allMdfs, mainSchemaIdOrLabel);

  const fieldMap: Record<string, FieldDefinition> = {};
  for (const schema of usedSchemas) {
    for (const field of schema.fields ?? []) {

      const isMainSchema = mainSchemaIdOrLabel === schema.id || mainSchemaIdOrLabel === schema.label;
      // Instead of fieldID here we are using field.id to ensure that all fields are present in the fieldMap.
      // Since field.fieldId can be duplicated across subtype schemas, we need to ensure that we are not skipping any fields.
      if (!isMainSchema && fieldMap[field.id]) {
        continue;
      }

      fieldMap[field.id] = { ...field, schemaId: schema.id };
      fieldMap[field.fieldId] = { ...field, schemaId: schema.id };
    }
  }

  return fieldMap;
}

